import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, ApplicationState } from '../../store';
import AsyncStatus from '../../model/common/AsyncStatus';
import Loading from '../Loading';
import { fetchDocuments, selectAll, createDocument, getDocument, deleteDocument } from '../../store/Documents';
import Document from '../../model/common/Document';
import {Row, Col} from 'reactstrap';
import { DocumentType } from '../../model/common/DocumentType';
import moment from 'moment';
import { Button, Section, SubSection, Message } from '../ui';
import { DocumentTypeSelect, FileField, TextAreaField, TextField } from '../fields';
import { IFieldEvent } from '../fields/interfaces';

const initialNewDocumentsState: {
  newDocumentFile?: File,
  newDocumentType?: string,
  newDocumentName?: string,
  newDocumentDescription?: string
} = {
  newDocumentFile: undefined,
  newDocumentType: "0",
  newDocumentName: undefined,
  newDocumentDescription: undefined
};

const initialDocumentsActionState: {
  [id: number]: boolean
} = {};

const DocumentsPage = function() {
  const dispatch : AppDispatch = useDispatch();
  const fetchStatus = useSelector<ApplicationState, AsyncStatus>(state => state.documents.fetchStatus);
  const createStatus = useSelector<ApplicationState, AsyncStatus>(state => state.documents.createStatus);
  const createError = useSelector<ApplicationState, string[] | undefined>(state => state.documents.createError);
  const documents = useSelector(selectAll);
  const [isAdding, setIsAdding] = useState(false);
  const [newDocument, setNewDocument] = useState(initialNewDocumentsState);
  const [documentActions, setDocumentActions] = useState(initialDocumentsActionState);

  // Fetch the documents if they haven't been fetched yet.
  useEffect(() => {
    if (fetchStatus === 'idle') {
      dispatch(fetchDocuments());
    }
  }, [fetchStatus, dispatch]);

  let handleNewDocumentInputChange = (e: IFieldEvent) => {
    setNewDocument({
      ...newDocument,
      [e.name]: e.value
    });
  }

  let handleNewDocumentFileChange = (e: React.FormEvent<HTMLInputElement>) => {
    let newFile = e.currentTarget.files?.[0];
    let newName = newDocument.newDocumentName;
    if (newDocument.newDocumentName == undefined || newDocument.newDocumentName === newDocument.newDocumentFile?.name) {
      newName = newFile?.name;
    }
    setNewDocument({
      ...newDocument,
      newDocumentName: newName,
      newDocumentFile: newFile
    });
  }

  let submitNewDocument = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const resultAction = await dispatch(createDocument({
      documentType: newDocument.newDocumentType as string,
      document: newDocument.newDocumentFile as File,
      description: newDocument.newDocumentDescription,
      name: newDocument.newDocumentName
    }));
    if (createDocument.fulfilled.match(resultAction)) {
      setNewDocument(initialNewDocumentsState);
      setIsAdding(false);
    }
  };

  let handleDocumentGetClick = async (id: number) => {
    setDocumentActions({...documentActions, [id]: true});
    await dispatch(getDocument({id}));
    setDocumentActions({...documentActions, [id]: false});
  }

  let handleDocumentDeleteClick = async (id: number) => {
    setDocumentActions({...documentActions, [id]: true});
    await dispatch(deleteDocument({id}));
    setDocumentActions({...documentActions, [id]: false});
  }

  //Alias the window propert so we do not have issues with using window.config that typescript doesn't recognize.
  const windowAlias: any = window;

  return (
    <Loading isLoading={fetchStatus === 'idle' || fetchStatus === 'loading'}>
      <Section name={"Documents"} addDisabled={isAdding} onAdd={() => {setIsAdding(true);}}>
        {fetchStatus === 'failed' && (
          <h1>Error loading documents!</h1>
        )}
        {fetchStatus === 'succeeded'&& (
          <>
            {isAdding && (
              <SubSection title="New Document" onRemove={() => {setIsAdding(false);}}>
                <Loading isLoading={createStatus === 'loading'}>
                  { createError != undefined && (
                    <Row>
                      <Col xs={12}>
                        <Message style="danger" title="Error creating new document" body={createError} />
                      </Col>
                    </Row>
                  )}
                  <form onSubmit={submitNewDocument}>
                    <Row>
                      <Col xs={12} lg={6}>
                        <FileField id={"newDocumentFile"} label="Choose a file" onChange={handleNewDocumentFileChange} required={true} allowedFileTypes={windowAlias.config.options.allowedFileTypes} />
                      </Col>
                      <Col xs={12} lg={6}>
                        <DocumentTypeSelect value={newDocument.newDocumentType} id={"newDocumentType"} label="Type" onChange={handleNewDocumentInputChange} required={true} />
                      </Col>
                      <Col xs={12} lg={6}>
                        <TextField value={newDocument.newDocumentName} id={"newDocumentName"} label="Name" onChange={handleNewDocumentInputChange} required={true} />
                      </Col>
                      <Col xs={12} lg={6}>
                        <TextAreaField value={newDocument.newDocumentDescription} id={"newDocumentDescription"} label="Description" onChange={handleNewDocumentInputChange} />
                      </Col>
                      <Col xs={12} className="text-right">
                        <Button isSubmit={true} label="Submit" />
                      </Col>
                      <Col xs={12}>
                        <p>This system converts imported documents into PDF format to improve ease of viewing. Please review your document after import to ensure that the formatting is acceptable. An employer will view the document in PDF format.</p>
                      </Col>
                    </Row>
                  </form>
                </Loading>
              </SubSection>
            )}
            <Row>
              {documents.length === 0 && (
                <Col xs={12}>
                  <h4>No documents yet.</h4>
                </Col>
              )}
              {documents.length > 0 && documents.map((d:Document, i: number) => (
                <Col key={i} xs={12} md={6} lg={3} className="pb-1">
                  <Loading isLoading={documentActions[d.id]}>
                    <div className="hcs-message">
                      <strong className="hcs-message__title">
                        <a href="" onClick={(e) => {e.preventDefault(); handleDocumentGetClick(d.id);}}>{d.name}</a>
                      </strong>
                      <p className="hcs-message__body">
                        Type: {DocumentType[d.documentType]}<br />
                        Created: {moment(d.createdOn).format('lll')}
                        {d.description != undefined && (
                          <>
                            <br />
                            Description: {d.description}
                          </>)}
                      </p>
                      <Button style="danger" label="Delete" onClick={() => {handleDocumentDeleteClick(d.id);}} />
                    </div>
                  </Loading>
                </Col>
              ))}
            </Row>
          </>
        )}
      </Section>
    </Loading>
  );
}

export default DocumentsPage;